import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, MapPin, Heart, CreditCard, Users, Check, X, AlertCircle, Gift, HandHeart, Briefcase, ArrowRight } from "lucide-react";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Progress } from "@/components/ui/progress";
import { getPostLoginRedirect } from "@/lib/role-routes";

function getPasswordStrength(password: string): { score: number; label: string; checks: { hasLength: boolean; hasUpper: boolean; hasLower: boolean; hasNumber: boolean } } {
  const checks = {
    hasLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
  
  const passedChecks = Object.values(checks).filter(Boolean).length;
  
  if (passedChecks <= 1) return { score: 25, label: "auth.passwordWeak", checks };
  if (passedChecks === 2) return { score: 50, label: "auth.passwordFair", checks };
  if (passedChecks === 3) return { score: 75, label: "auth.passwordGood", checks };
  return { score: 100, label: "auth.passwordStrong", checks };
}

function createLoginSchema(t: (key: string) => string) {
  return z.object({
    username: z.string().min(1, t('auth.zodUsernameRequired')),
    password: z.string().min(1, t('auth.zodPasswordRequired')),
  });
}

function createRegisterSchema(t: (key: string) => string) {
  return z.object({
    username: z.string().min(3, t('auth.zodUsernameMinLength')),
    password: z.string()
      .min(8, t('auth.zodPasswordMinLength'))
      .regex(/[A-Z]/, t('auth.zodPasswordUppercase'))
      .regex(/[a-z]/, t('auth.zodPasswordLowercase'))
      .regex(/[0-9]/, t('auth.zodPasswordNumber')),
    confirmPassword: z.string(),
    email: z.string().email(t('auth.zodEmailInvalid')),
    firstName: z.string().min(1, t('auth.zodFirstNameRequired')),
    lastName: z.string().min(1, t('auth.zodLastNameRequired')),
    role: z.enum(["patient", "supporter", "driver", "dispatcher"], {
      required_error: t('auth.zodRoleRequired'),
    }),
    acceptTerms: z.boolean().refine(val => val === true, {
      message: t('auth.zodTermsRequired'),
    }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('auth.zodPasswordsMismatch'),
    path: ["confirmPassword"],
  });
}

type LoginFormData = {
  username: string;
  password: string;
};

type RegisterFormData = {
  username: string;
  password: string;
  confirmPassword: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "patient" | "supporter" | "driver" | "dispatcher";
  acceptTerms: boolean;
};

export default function AuthPage() {
  usePageTitle("Sign In or Register", "Sign in or create your HealthWallet account to access community-powered healthcare funding.");
  const [, setLocation] = useLocation();
  const { user, loginMutation, registerMutation } = useAuth();
  const [activeTab, setActiveTab] = useState<"login" | "register">("login");
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get('ref');
    const refFromStorage = sessionStorage.getItem('referralCode');
    const code = refFromUrl || refFromStorage;
    if (code) {
      setReferralCode(code);
      sessionStorage.setItem('referralCode', code);
      setActiveTab("register");
    }
    const tabParam = params.get('tab');
    if (tabParam === 'register') {
      setActiveTab("register");
    }
  }, []);

  const loginForm = useForm<LoginFormData>({
    mode: 'onBlur',
    resolver: (...args) => zodResolver(createLoginSchema(tRef.current))(...args),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const registerForm = useForm<RegisterFormData>({
    mode: 'onBlur',
    resolver: (...args) => zodResolver(createRegisterSchema(tRef.current))(...args),
    defaultValues: {
      username: "",
      password: "",
      confirmPassword: "",
      email: "",
      firstName: "",
      lastName: "",
      role: "patient",
      acceptTerms: false,
    },
  });
  
  const watchPassword = registerForm.watch("password");
  const passwordStrength = useMemo(() => getPasswordStrength(watchPassword || ""), [watchPassword]);

  useEffect(() => {
    const loginErrorFields = Object.keys(loginForm.formState.errors) as (keyof LoginFormData)[];
    if (loginErrorFields.length > 0) {
      loginForm.trigger(loginErrorFields);
    }
    const registerErrorFields = Object.keys(registerForm.formState.errors) as (keyof RegisterFormData)[];
    if (registerErrorFields.length > 0) {
      registerForm.trigger(registerErrorFields);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (user) {
      const params = new URLSearchParams(window.location.search);
      const returnTo = params.get('returnTo');
      if (returnTo && returnTo.startsWith('/')) {
        setLocation(returnTo);
        return;
      }
      setLocation(getPostLoginRedirect(user.role, user.onboardingCompleted));
    }
  }, [user, setLocation]);

  const onLogin = async (data: LoginFormData) => {
    try {
      await loginMutation.mutateAsync(data);
    } catch {
    }
  };

  const onRegister = async (data: RegisterFormData) => {
    try {
      const { confirmPassword, acceptTerms, ...registerData } = data;
      await registerMutation.mutateAsync({
        ...registerData,
        referralCode: referralCode || undefined,
      } as any);
      sessionStorage.removeItem('referralCode');
    } catch {
    }
  };

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen flex relative" data-testid="auth-page">
      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="flex-1 flex flex-col overflow-y-auto bg-background">
        <div className="my-auto w-full max-w-md mx-auto p-4 sm:p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src={healthWalletLogo} 
                alt="HealthWallet Logo" 
                className="h-12 w-12 object-contain"
                width="1024"
                height="1024"
                data-testid="img-auth-logo"
              />
              <h1 className="text-2xl sm:text-3xl font-bold">{t('app.name')}</h1>
            </div>
            <p className="text-muted-foreground">
              {t('app.tagline')}
            </p>
          </div>

          <Card className="mb-4 border-primary/20 bg-primary/5 cursor-pointer hover-elevate" onClick={() => setLocation("/small-business/signup")} data-testid="card-business-owner-cta">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10 shrink-0">
                  <Briefcase className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">Are you a small business owner?</p>
                  <p className="text-xs text-muted-foreground">Set up health benefits for your team in minutes</p>
                </div>
                <ArrowRight className="w-4 h-4 text-primary shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "login" | "register")}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" data-testid="tab-login">{t('auth.signIn')}</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">{t('auth.register')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent>
              {activeTab === "login" ? (
                <Form {...loginForm}>
                  <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                    <FormField
                      control={loginForm.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.username')}</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder={t('auth.username')} 
                              data-testid="input-login-username"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.password')}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder={t('auth.password')} 
                              data-testid="input-login-password"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={loginMutation.isPending}
                      data-testid="button-login-submit"
                    >
                      {loginMutation.isPending ? t('common.loading') : t('auth.signIn')}
                    </Button>
                  </form>
                </Form>
              ) : (
                <Form {...registerForm}>
                  {referralCode && (
                    <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20" data-testid="banner-referral-info">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">
                          You were invited by a friend! You'll receive a $10 welcome bonus after signing up.
                        </span>
                      </div>
                    </div>
                  )}
                  <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-4">
                    <FormField
                      control={registerForm.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-semibold mb-3">{t('auth.roleQuestion')}</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="grid grid-cols-1 gap-3 pt-2"
                            >
                              <div>
                                <RadioGroupItem
                                  value="patient"
                                  id="role-patient"
                                  className="peer sr-only"
                                  data-testid="radio-role-patient"
                                />
                                <label
                                  htmlFor="role-patient"
                                  className="flex items-start gap-3 rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5 transition-colors"
                                >
                                  <div className="flex-shrink-0 mt-1">
                                    <Heart className="h-5 w-5 text-primary" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{t('auth.rolePatient')}</div>
                                    <div className="text-xs text-muted-foreground">{t('auth.rolePatientDesc')}</div>
                                  </div>
                                </label>
                              </div>
                              <div>
                                <RadioGroupItem
                                  value="supporter"
                                  id="role-supporter"
                                  className="peer sr-only"
                                  data-testid="radio-role-supporter"
                                />
                                <label
                                  htmlFor="role-supporter"
                                  className="flex items-start gap-3 rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5 transition-colors"
                                >
                                  <div className="flex-shrink-0 mt-1">
                                    <HandHeart className="h-5 w-5 text-primary" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{t('auth.roleSupporter')}</div>
                                    <div className="text-xs text-muted-foreground">{t('auth.roleSupporterDesc')}</div>
                                  </div>
                                </label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={registerForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('auth.firstName')}</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder={t('auth.firstName')} 
                                autoComplete="given-name"
                                data-testid="input-register-firstname"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={registerForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('auth.lastName')}</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder={t('auth.lastName')} 
                                autoComplete="family-name"
                                data-testid="input-register-lastname"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="register-email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {t('auth.email')}
                        </label>
                        <Input 
                          id="register-email"
                          placeholder={t('auth.email')}
                          autoComplete="off"
                          data-testid="input-register-email"
                          {...registerForm.register("email")}
                        />
                        {registerForm.formState.errors.email && (
                          <p className="text-sm font-medium text-destructive">
                            {registerForm.formState.errors.email.message}
                          </p>
                        )}
                      </div>
                      <FormField
                        control={registerForm.control}
                        name="username"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('auth.username')}</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder={t('auth.username')} 
                                autoComplete="username"
                                data-testid="input-register-username"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={registerForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.password')}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder={t('auth.passwordPlaceholder')} 
                              autoComplete="new-password"
                              data-testid="input-register-password"
                              {...field} 
                            />
                          </FormControl>
                          {field.value && (
                            <div className="space-y-2 pt-1">
                              <div className="flex items-center gap-2">
                                <Progress value={passwordStrength.score} className="h-1.5 flex-1" />
                                <span className={`text-xs font-medium ${
                                  passwordStrength.score <= 50 ? "text-destructive" : "text-primary"
                                }`}>
                                  {t(passwordStrength.label)}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                                <div className={`flex items-center gap-1 ${passwordStrength.checks.hasLength ? "text-primary" : "text-muted-foreground"}`}>
                                  {passwordStrength.checks.hasLength ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                                  {t('auth.passwordMinLength')}
                                </div>
                                <div className={`flex items-center gap-1 ${passwordStrength.checks.hasUpper ? "text-primary" : "text-muted-foreground"}`}>
                                  {passwordStrength.checks.hasUpper ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                                  {t('auth.passwordUppercase')}
                                </div>
                                <div className={`flex items-center gap-1 ${passwordStrength.checks.hasLower ? "text-primary" : "text-muted-foreground"}`}>
                                  {passwordStrength.checks.hasLower ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                                  {t('auth.passwordLowercase')}
                                </div>
                                <div className={`flex items-center gap-1 ${passwordStrength.checks.hasNumber ? "text-primary" : "text-muted-foreground"}`}>
                                  {passwordStrength.checks.hasNumber ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                                  {t('auth.passwordNumber')}
                                </div>
                              </div>
                            </div>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.confirmPassword')}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder={t('auth.confirmPasswordPlaceholder')} 
                              autoComplete="new-password"
                              data-testid="input-register-confirm-password"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="acceptTerms"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3 bg-muted/30">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              onBlur={field.onBlur}
                              ref={field.ref}
                              id="accept-terms"
                              aria-describedby="accept-terms-description"
                              data-testid="checkbox-accept-terms"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <label htmlFor="accept-terms" className="text-sm font-normal cursor-pointer">
                              <Trans
                                i18nKey="auth.agreeToTerms"
                                components={{
                                  termsLink: <a href="/terms" className="text-primary underline" target="_blank" rel="noopener noreferrer" />,
                                  privacyLink: <a href="/privacy" className="text-primary underline" target="_blank" rel="noopener noreferrer" />
                                }}
                              />
                            </label>
                            <p id="accept-terms-description" className="sr-only">
                              {t('auth.agreeSrText')}
                            </p>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={registerMutation.isPending}
                      data-testid="button-register-submit"
                    >
                      {registerMutation.isPending ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          {t('auth.creatingAccount')}
                        </span>
                      ) : (
                        t('auth.createAccount')
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/5 via-background to-primary/5 items-center justify-center p-12">
        <div className="max-w-md">
          <h2 className="text-3xl font-bold mb-2">
            {t('auth.marketingHeadline')}
          </h2>
          <p className="text-lg text-muted-foreground mb-2">
            {t('auth.marketingSubtitle')}
          </p>
          <p className="text-muted-foreground mb-8">
            {t('auth.marketingDesc')}
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Heart className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium">{t('auth.featureCommunityTitle')}</div>
                <div className="text-sm text-muted-foreground">{t('auth.featureCommunityDesc')}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium">{t('auth.featurePaymentTitle')}</div>
                <div className="text-sm text-muted-foreground">{t('auth.featurePaymentDesc')}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium">{t('auth.featureConditionTitle')}</div>
                <div className="text-sm text-muted-foreground">{t('auth.featureConditionDesc')}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium">{t('auth.featureSecurityTitle')}</div>
                <div className="text-sm text-muted-foreground">{t('auth.featureSecurityDesc')}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium">{t('auth.featureWalletTitle')}</div>
                <div className="text-sm text-muted-foreground">{t('auth.featureWalletDesc')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
